import { inject, Injectable } from '@angular/core';

import { CanvasElementType, ElementTransformData } from '@blaze-runtime/engine-domain';
import { type ColumnDataType } from '@blaze-shared/api-contracts-domain';
import { compareAscBy } from '@blaze-shared/platform-util';
import { SelectOption } from '@blaze-shared/shared-util';

import { ElementDefinitionsService } from './element-definitions.service';

type TransformationEntry<SourceType extends CanvasElementType> = Record<
  SourceType,
  ElementTransformData<SourceType, CanvasElementType>[]
>;

type TransformationMap = Partial<TransformationEntry<CanvasElementType>>;

/** Narrows the transformation list to the targets applicable in the caller's context. */
export interface TransformationListQuery {
  /**
   * Data type of the column the Search & Filters field filters on.
   * Targets restricting themselves via `searchAndFiltersColumnTypes` are dropped unless they accept it.
   * Left out where the filtered column is unknown, which skips that narrowing.
   */
  filterColumnDataType?: ColumnDataType;

  /** Whether the source element is readonly - gates the `textInput` -> `textField` transformation. */
  isElementReadonly?: boolean;

  /** Whether the source element is a Search & Filters field, which only accepts controls supporting it. */
  isSearchAndFilterField: boolean;

  /**
   * Whether the source type itself is listed, so a select bound to it can display its own value.
   * @default true
   */
  includeCurrent?: boolean;
}

@Injectable({ providedIn: 'root' })
export class ElementTransformationService {
  private readonly elementDefinitionsService = inject(ElementDefinitionsService);

  private transformationMap: TransformationMap = {};

  canTransform(sourceType: CanvasElementType, targetType: CanvasElementType): boolean {
    return !!this.transformationMap[sourceType]?.some((td) => td.targetType === targetType);
  }

  getTransformationList<SourceType extends CanvasElementType>(
    sourceType: SourceType,
  ): TransformationMap[SourceType] | undefined {
    return this.transformationMap[sourceType];
  }

  getTransformationListOptions<SourceType extends CanvasElementType>(
    sourceType: SourceType,
    {
      filterColumnDataType,
      isElementReadonly,
      isSearchAndFilterField,
      includeCurrent = true,
    }: TransformationListQuery,
  ): SelectOption<string>[] {
    const results =
      this.getTransformationList(sourceType)
        ?.filter((transformation) => {
          // Allow transformation textInput to textField only when it is readonly.
          if (sourceType === 'textInput' && transformation.targetType === 'textField') {
            return isElementReadonly;
          }

          // Search & Filters have narrowed down options
          if (isSearchAndFilterField) {
            return this.canFilterColumn(transformation.targetType, filterColumnDataType);
          }

          return true;
        })
        .map<SelectOption<string>>(({ targetType }) =>
          mapTypeToSelectOption(targetType, this.elementDefinitionsService),
        ) ?? [];

    if (includeCurrent) {
      results.push(mapTypeToSelectOption(sourceType, this.elementDefinitionsService));
    }
    return results.toSorted(compareAscBy('label'));
  }

  getTransformation<SourceType extends CanvasElementType, TargetType extends CanvasElementType>(
    sourceType: SourceType,
    targetType: TargetType,
  ): ElementTransformData<SourceType, TargetType> | undefined {
    const transformationData = this.transformationMap[sourceType]?.find(
      (td) => td.targetType === targetType,
    );
    if (!transformationData || !isTransformationMatch(transformationData, sourceType, targetType)) {
      return;
    }
    return transformationData;
  }

  registerTransformation<
    SourceType extends CanvasElementType,
    TargetType extends CanvasElementType,
  >(transformData: ElementTransformData<SourceType, TargetType>): void {
    const sourceType = transformData.sourceType;

    // Check if transformation not added yet
    if (
      this.transformationMap[sourceType]?.find((td) => td.targetType === transformData.targetType)
    ) {
      throw new Error(
        `Transformation ${sourceType}->${transformData.targetType} already registered`,
      );
    }

    if (!this.transformationMap[sourceType]) {
      this.transformationMap[sourceType] = [];
    }

    const transformationsForSourceType = this.transformationMap[sourceType] as unknown as
      | ElementTransformData<SourceType, CanvasElementType>[]
      | undefined;
    if (!transformationsForSourceType) {
      throw new Error('Transformation array missing');
    }
    transformationsForSourceType.push(transformData);
  }

  /**
   * Tells whether the element can act as the Search & Filters control of the filtered column.
   * An element restricting itself to some column data types is only rejected once that type is known.
   */
  private canFilterColumn(
    elementType: CanvasElementType,
    filterColumnDataType: ColumnDataType | undefined,
  ): boolean {
    if (
      this.elementDefinitionsService.getFeature(elementType, 'searchAndFiltersFieldSupport') !==
      true
    ) {
      return false;
    }

    const supportedColumnTypes = this.elementDefinitionsService.getFeature(
      elementType,
      'searchAndFiltersColumnTypes',
    );

    return (
      !supportedColumnTypes ||
      !filterColumnDataType ||
      supportedColumnTypes.includes(filterColumnDataType)
    );
  }
}

function isTransformationMatch<
  SourceType extends CanvasElementType,
  TargetType extends CanvasElementType,
>(
  transformationData: ElementTransformData<any, any>,
  sourceType: SourceType,
  targetType: TargetType,
): transformationData is ElementTransformData<SourceType, TargetType> {
  return (
    transformationData.sourceType === sourceType && transformationData.targetType === targetType
  );
}

function mapTypeToSelectOption(
  elementType: CanvasElementType,
  elementDefinitionsService: ElementDefinitionsService,
): SelectOption<string> {
  return {
    value: elementType,
    label: elementDefinitionsService.getDefinition(elementType)?.name ?? elementType,
    icon: `ei-${elementType}`,
  };
}
